import type { Squad, InvoiceLineItem } from '../../types';
import { programTermForDate, resolveProgramTermId } from '../terms/programTerms';

export type NormalizedSquadPaymentMode = 'stripe' | 'school_flat_fee' | 'school_enrollment';

export function normalizeSquadPaymentMode(raw: any): NormalizedSquadPaymentMode {
  const v = String(raw || '').trim().toLowerCase();
  if (v === 'school_flat_fee') return 'school_flat_fee';
  if (v === 'school_enrollment') return 'school_enrollment';
  if (v === 'external') return 'school_enrollment'; // legacy mapping
  return 'stripe';
}

export function termIdForSquadStartDate(sq: Squad): string | null {
  const sd = String(sq.schedule?.mainDate || sq.schedule?.startDate || '').trim();
  if (!sd) return typeof (sq as any).termId === 'string' ? String((sq as any).termId) : null;
  // Interpret YYYY-MM-DD in local timezone; this is good enough for program-term bucketing.
  const d = new Date(`${sd}T00:00:00`);
  if (Number.isNaN(d.getTime())) return typeof (sq as any).termId === 'string' ? String((sq as any).termId) : null;
  return programTermForDate(d).id;
}

/**
 * Resolve a squad's canonical billing term without allowing a calendar-date
 * guess to override explicit or legacy season metadata.
 */
export function resolveSquadTermId(sq: Squad): string | null {
  const raw = sq as any;
  const explicitRaw = raw.termId || raw.seasonId || raw.term;
  const explicit = resolveProgramTermId({
    termId: explicitRaw,
    legacyLabel: raw.seasonLabel || raw.termLabel || raw.season || explicitRaw,
  });
  return explicit || termIdForSquadStartDate(sq);
}

function parseYmd(v: string): number {
  // For sorting only: YYYY-MM-DD -> YYYYMMDD numeric
  const s = String(v || '').trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return Number.POSITIVE_INFINITY;
  return Number(m[1] + m[2] + m[3]);
}

export function computeServiceRangeFromSquads(squads: Squad[]): { startDate: string; endDate: string } | null {
  const starts = squads.map(s => String(s.schedule?.mainDate || s.schedule?.startDate || '').trim()).filter(Boolean).sort((a, b) => parseYmd(a) - parseYmd(b));
  const ends = squads.map(s => String(s.schedule?.mainDate || s.schedule?.endDate || '').trim()).filter(Boolean).sort((a, b) => parseYmd(a) - parseYmd(b));
  if (starts.length === 0 || ends.length === 0) return null;
  return { startDate: starts[0], endDate: ends[ends.length - 1] };
}

function dayKeyToJsDow(dayOfWeek?: string): number | null {
  const s = String(dayOfWeek || '').trim().toUpperCase();
  const map: Record<string, number> = { SUN: 0, MON: 1, TUE: 2, WED: 3, THU: 4, FRI: 5, SAT: 6 };
  return map[s] ?? null;
}

/**
 * Best-effort count of scheduled class occurrences between startDate/endDate.
 * - Counts weekly occurrences on schedule.dayOfWeek
 * - Subtracts exceptions (dates that fall on the schedule DOW within range)
 * - Adds makeups (dates within range)
 */
export function computeTotalClassesFromSchedule(schedule: Squad['schedule']): number | null {
  if (String(schedule?.mainDate || '').trim()) return 1;
  const start = String(schedule?.startDate || '').trim();
  const end = String(schedule?.endDate || '').trim();
  const dow = dayKeyToJsDow(schedule?.dayOfWeek);
  if (!start || !end || dow == null) return null;
  const startD = new Date(`${start}T00:00:00`);
  const endD = new Date(`${end}T00:00:00`);
  if (Number.isNaN(startD.getTime()) || Number.isNaN(endD.getTime())) return null;
  if (endD.getTime() < startD.getTime()) return null;

  // Find first occurrence on/after start
  const first = new Date(startD);
  while (first.getDay() !== dow) first.setDate(first.getDate() + 1);
  if (first.getTime() > endD.getTime()) return 0;

  const msWeek = 7 * 24 * 60 * 60 * 1000;
  const weeks = Math.floor((endD.getTime() - first.getTime()) / msWeek);
  let count = weeks + 1; // include first

  const exceptions = Array.from(new Set([
    ...(Array.isArray(schedule?.exceptions) ? schedule!.exceptions : []),
    ...(Array.isArray(schedule?.inheritedExceptions) ? schedule!.inheritedExceptions! : []),
  ].map(String)));
  const makeups = Array.isArray(schedule?.makeups) ? schedule!.makeups.map(String) : [];

  const inRange = (d: string) => {
    const t = parseYmd(d);
    if (!Number.isFinite(t)) return false;
    return parseYmd(start) <= t && t <= parseYmd(end);
  };

  // Exceptions: only subtract if they match scheduled day of week and are in range.
  for (const ex of exceptions) {
    if (!inRange(ex)) continue;
    const exD = new Date(`${ex}T00:00:00`);
    if (Number.isNaN(exD.getTime())) continue;
    if (exD.getDay() === dow) count -= 1;
  }

  // Makeups: add if in range (regardless of DOW; these are additional classes)
  for (const mu of makeups) {
    if (!inRange(mu)) continue;
    count += 1;
  }

  return Math.max(0, count);
}

export function buildInvoiceLineItemsFromSquads(params: {
  squads: Squad[];
}): InvoiceLineItem[] {
  const out: InvoiceLineItem[] = [];
  for (const sq of params.squads) {
    const mode = normalizeSquadPaymentMode((sq as any).paymentMode);
    if (mode === 'stripe') continue; // not billed to school

    // Pricing:
    // - Enrollment-based: per-student per-class (tuitionPerStudentPerClassCents preferred; falls back to legacy priceCents)
    // - Flat fee: per-class flat fee (flatFeePerClassCents preferred; falls back to legacy priceCents as a TOTAL session fee)
    const legacyPriceCents = Number.isFinite(Number((sq as any).priceCents)) ? Number((sq as any).priceCents) : 0;
    const tuitionPerStudentPerClassCents =
      Number.isFinite(Number((sq as any).tuitionPerStudentPerClassCents))
        ? Number((sq as any).tuitionPerStudentPerClassCents)
        : legacyPriceCents;
    const flatFeePerClassCents =
      Number.isFinite(Number((sq as any).flatFeePerClassCents))
        ? Number((sq as any).flatFeePerClassCents)
        : null;
    const enrolled = Array.isArray((sq as any).studentIds) ? (sq as any).studentIds.length : 0;

    const startDate = String(sq.schedule?.mainDate || sq.schedule?.startDate || '').trim();
    const endDate = String(sq.schedule?.mainDate || sq.schedule?.endDate || '').trim();
    const classesCount =
      Number.isFinite(Number((sq as any).totalClasses)) && Number((sq as any).totalClasses) > 0
        ? Number((sq as any).totalClasses)
        : (computeTotalClassesFromSchedule(sq.schedule) ?? undefined);

    // Billing convention:
    // - Enrollment-based: per-class per-student. Rate shown on invoice is per-student session cost: (tuition per class) * #classes.
    // - Flat fee:
    //   - Preferred: per-class flat fee. We convert to a total session fee for invoicing by multiplying by #classes.
    //   - Legacy: priceCents is treated as a total session fee.
    const classes = Number.isFinite(Number(classesCount)) && Number(classesCount) > 0 ? Number(classesCount) : 1;
    const qty = mode === 'school_flat_fee' ? 1 : Math.max(0, enrolled);
    const rate =
      mode === 'school_enrollment'
        ? Math.max(0, Math.round(tuitionPerStudentPerClassCents)) * classes
        : Math.max(0, Math.round(flatFeePerClassCents != null ? flatFeePerClassCents * classes : legacyPriceCents));
    const amount = rate * qty;

    const title = String(sq.title || '').trim();
    const descPieces = [
      title || 'Program tuition',
      (classesCount != null) ? `(${Number(classesCount)} classes)` : '',
    ].filter(Boolean);

    out.push({
      description: descPieces.join(' '),
      rateCents: rate,
      quantity: qty,
      amountCents: amount,
      squadId: sq.id,
      squadTitle: sq.title,
      paymentMode: mode,
      serviceStartDate: startDate || undefined,
      serviceEndDate: endDate || undefined,
      classesCount: classesCount != null ? Number(classesCount) : undefined,
    });
  }

  // Stable ordering for readability: by start date then title
  out.sort((a, b) => {
    const as = parseYmd(a.serviceStartDate || '');
    const bs = parseYmd(b.serviceStartDate || '');
    if (as !== bs) return as - bs;
    return String(a.squadTitle || '').localeCompare(String(b.squadTitle || ''));
  });

  return out;
}
